/**
 * 师资管理API服务
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787';

// ============================
// 接口类型定义
// ============================

export interface Faculty {
  id: number;
  category: string;
  region: string;
  name: string;
  gender: '男' | '女';
  introduction?: string;
  residence?: string;
  remarks?: string;
  certificate_category?: string;
  certificate_level?: string;
  certificate_name?: string;
  teacher_certificate?: string;
  phone?: string;
  daily_fee?: number;
  local_evaluation?: string;
  id_card_file_path?: string;
  title_cert_file_path?: string;
  qualification_cert_file_path?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFacultyInput {
  category: string;
  region: string;
  name: string;
  gender: '男' | '女';
  introduction?: string;
  residence?: string;
  remarks?: string;
  certificate_category?: string;
  certificate_level?: string;
  certificate_name?: string;
  teacher_certificate?: string;
  phone?: string;
  daily_fee?: number;
  local_evaluation?: string;
  id_card_file_path?: string;
  title_cert_file_path?: string;
  qualification_cert_file_path?: string;
}

export interface FacultyQuery {
  page?: number;
  size?: number;
  category?: string;
  region?: string;
  name?: string;
  gender?: string;
  certificate_category?: string;
  certificate_level?: string;
  residence?: string;
  order_by?: string;
  order_direction?: 'asc' | 'desc';
}

export interface FacultyListResult {
  data: Faculty[];
  total: number;
  page: number;
  size: number;
}

export interface FacultyCategory {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CertificateType {
  id: number;
  category: string;
  level: string;
  organization: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FacultyAnalytics {
  total_faculty: number;
  total_categories: number;
  total_regions: number;
  certified_faculty: number;
  avg_daily_fee?: number;
  max_daily_fee?: number;
  min_daily_fee?: number;
  category_distribution: Array<{ category: string; count: number }>;
  region_distribution: Array<{ region: string; count: number }>;
  certificate_distribution: Array<{ certificate_category: string; count: number }>;
}

export interface UploadResult {
  url: string;
  key: string;
  bucket: string;
  size: number;
  contentType: string;
}

export interface FileInfo {
  key: string;
  size: number;
  lastModified: string;
  contentType: string;
  url: string;
}

// ============================
// API响应类型
// ============================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp: string;
}

// ============================
// 师资管理服务
// ============================

class FacultyService {
  /**
   * 获取师资列表
   */
  async getFacultyList(query: FacultyQuery = {}): Promise<FacultyListResult> {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await axios.get<ApiResponse<FacultyListResult>>(
      `${API_BASE_URL}/api/faculty?${params.toString()}`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取师资列表失败');
    }
  }

  /**
   * 获取师资详情
   */
  async getFacultyById(id: number): Promise<Faculty> {
    const response = await axios.get<ApiResponse<Faculty>>(
      `${API_BASE_URL}/api/faculty/${id}`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取师资详情失败');
    }
  }

  /**
   * 创建师资信息
   */
  async createFaculty(input: CreateFacultyInput): Promise<Faculty> {
    const response = await axios.post<ApiResponse<Faculty>>(
      `${API_BASE_URL}/api/faculty`,
      input
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '创建师资信息失败');
    }
  }

  /**
   * 更新师资信息
   */
  async updateFaculty(id: number, input: Partial<CreateFacultyInput>): Promise<Faculty> {
    const response = await axios.put<ApiResponse<Faculty>>(
      `${API_BASE_URL}/api/faculty/${id}`,
      input
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '更新师资信息失败');
    }
  }

  /**
   * 删除师资信息
   */
  async deleteFaculty(id: number): Promise<void> {
    const response = await axios.delete<ApiResponse<boolean>>(
      `${API_BASE_URL}/api/faculty/${id}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || '删除师资信息失败');
    }
  }

  /**
   * 获取师资类别列表
   */
  async getFacultyCategories(): Promise<FacultyCategory[]> {
    const response = await axios.get<ApiResponse<FacultyCategory[]>>(
      `${API_BASE_URL}/api/faculty/config/categories`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取师资类别失败');
    }
  }

  /**
   * 获取证书类别列表
   */
  async getCertificateTypes(category?: string): Promise<CertificateType[]> {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await axios.get<ApiResponse<CertificateType[]>>(
      `${API_BASE_URL}/api/faculty/config/certificates${params}`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取证书类别失败');
    }
  }

  /**
   * 根据证书类别获取级别列表
   */
  async getCertificateLevelsByCategory(category: string): Promise<CertificateType[]> {
    const response = await axios.get<ApiResponse<CertificateType[]>>(
      `${API_BASE_URL}/api/faculty/config/certificates/levels/${encodeURIComponent(category)}`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取证书级别失败');
    }
  }

  /**
   * 获取师资统计分析
   */
  async getAnalytics(): Promise<FacultyAnalytics> {
    const response = await axios.get<ApiResponse<FacultyAnalytics>>(
      `${API_BASE_URL}/api/faculty/analytics`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取师资统计失败');
    }
  }

  /**
   * 上传文件
   */
  async uploadFile(file: File, folder: string = 'faculty'): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await axios.post<ApiResponse<UploadResult>>(
      `${API_BASE_URL}/api/upload/file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '文件上传失败');
    }
  }

  /**
   * 删除文件
   */
  async deleteFile(key: string): Promise<void> {
    const encodedKey = encodeURIComponent(key);
    const response = await axios.delete<ApiResponse<{ deleted: boolean }>>(
      `${API_BASE_URL}/api/upload/file/${encodedKey}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || '文件删除失败');
    }
  }

  /**
   * 获取文件信息
   */
  async getFileInfo(key: string): Promise<FileInfo> {
    const encodedKey = encodeURIComponent(key);
    const response = await axios.get<ApiResponse<FileInfo>>(
      `${API_BASE_URL}/api/upload/file/${encodedKey}/info`
    );

    if (response.data.success) {
      return response.data.data!;
    } else {
      throw new Error(response.data.error || '获取文件信息失败');
    }
  }
}

// 导出单例
export const facultyService = new FacultyService();
export default facultyService;